import { INavigationLink } from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/INavigationLink";
import { NavigationLink } from "@faire/retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/NavigationLink";
import { trackRetailerEvent } from "@faire/retailer-visitor-shared/lib/trackRetailerEvent";
import { useCategoryNavigationPrefetch } from "@faire/retailer-visitor-shared/lib/useCategoryNavigationPrefetch";
import { cn } from "@faire/web/tools/cn";
import React from "react";

interface IProps {
  link: INavigationLink;
  selectedLinkName: string | undefined;
  onClick: (link: INavigationLink) => void;
  onMouseEnter: (link: INavigationLink) => void;
  tooltip?: React.ReactNode;
  className?: string;
  isUserInteractingWithTouch: boolean;
}

export const L1Link: React.FC<IProps> = ({
  link,
  selectedLinkName,
  onClick,
  onMouseEnter,
  className,
  tooltip,
  isUserInteractingWithTouch,
}) => {
  const CATEGORY_PREFETCH_SURFACE_TYPE = "topNav";
  const handleClick = React.useCallback(() => {
    onClick(link);
    trackRetailerEvent(`/header-categories/${link.name}`);
  }, [link, onClick]);
  const isSalesLink =
    link.categoryPathIdentifier === "sale" || link.isWarehouseSale;
  const {
    prefetchOnMouseEnter,
    prefetchOnTouchStart,
    clearPrefetchTimeoutOnBlur,
  } = useCategoryNavigationPrefetch(CATEGORY_PREFETCH_SURFACE_TYPE);

  const disableNavigation = isUserInteractingWithTouch && link.hasSublinks;

  const handleMouseEnter = React.useCallback(() => {
    onMouseEnter(link);
    prefetchOnMouseEnter(link.to);
  }, [link, onMouseEnter, prefetchOnMouseEnter]);

  const isSelected = link.name === selectedLinkName;
  const isTextOrange = isSalesLink && !isSelected;
  const isTextSubdued = selectedLinkName != null && !isSelected && !isSalesLink;

  return (
    <div
      aria-haspopup="menu"
      onMouseEnter={handleMouseEnter}
      onFocus={handleMouseEnter}
      onTouchStart={() => prefetchOnTouchStart(link.to)}
      onMouseLeave={clearPrefetchTimeoutOnBlur}
      className={cn(className, "flex", {
        "cursor-pointer": link.to && !disableNavigation,
      })}
    >
      <NavigationLink
        link={link}
        disableNavigation={disableNavigation}
        onClick={handleClick}
        dataTestId={`header-${
          link.categoryPathIdentifier ?? "category"
        }-dropdown-link`}
        className={cn(
          "font-fs-paragraph",
          "active:text-core-text-subdued",
          "focus:text-core-text-subdued",
          "border-b-1 border-solid border-transparent",
          "whitespace-nowrap",
          "text-core-text-default",
          {
            "border-b-core-text-default": isSelected,
            "text-decorative-shade-orange": isTextOrange,
            "text-core-text-subdued": isTextSubdued,
          }
        )}
      >
        {link?.name}
      </NavigationLink>
      {tooltip}
    </div>
  );
};
